'use client';

import { useState } from 'react';
import Chips from '@pixum/chip';
import type { MagazineGroupProps } from '../types';
import { Magazine } from './Magazine';
import { GroupedMagazineComponent } from './GroupedMagazine';
import clsx from 'clsx';
import { getMagazineVariant } from '../utils/getMagazineVariant';
import { createChipProps } from '../../../utils/props';
import type { MagazineNode, GroupedMagazineNode } from 'data-layer';

function isGroupedMagazineNode(item: MagazineNode | GroupedMagazineNode): item is GroupedMagazineNode {
  return item.transformerType === 'GroupedMagazineTransformer';
}

export function MagazineGroup(props: MagazineGroupProps) {
  const { magazines, device, groupIndex } = props;
  const [currentIndex, setCurrentIndex] = useState(0);

  const magazineNodes = magazines.filter((mag): mag is MagazineNode => !isGroupedMagazineNode(mag));
  const chipProps = createChipProps(magazineNodes);
  const isMobile = device === 'mobile';

  // Create a stable ID for the group
  const groupId = `magazine-group-${groupIndex}`;
  const activeMagazine = magazineNodes[currentIndex];
  const ariaLabel = activeMagazine?.headline ? `Magazine group: ${activeMagazine.headline}` : 'Magazine group';
  const hasNonGroupedMagazines = magazineNodes.length > 0;

  const children = (
    <>
      {props.magazines.map((magazine, index) => {
        if (isGroupedMagazineNode(magazine)) {
          return (
            <GroupedMagazineComponent
              key={`grouped-magazine-${index}`}
              className="grouped-magazine"
              groupedMagazine={magazine}
              device={device}
              groupIndex={groupIndex}
            />
          );
        }

        const activeIndex = magazineNodes.findIndex(mn => mn.id === magazine.id);
        const isActive = currentIndex === activeIndex;
        const magazineId = `${groupId}-item-${index}`;

        return (
          <Magazine
            className={clsx('magazine-group-item', isActive ? 'magazine-group-item--active' : 'magazine--hide')}
            key={`magazine-group-${index}`}
            chips={
              <Chips
                activeIndex={currentIndex}
                chips={chipProps}
                chipsHeadline="" // we need PXMCY-21723 to add a headline
                onSelectedChipChange={setCurrentIndex}
              />
            }
            {...magazine}
            contentVariant={getMagazineVariant(groupIndex)}
            isMobile={isMobile}
            device={device}
            id={magazineId}
            aria-hidden={!isActive}
          />
        );
      })}
    </>
  );

  return hasNonGroupedMagazines ? (
    <section className="magazine-group-container" aria-label={ariaLabel} role="region">
      {children}
    </section>
  ) : (
    children
  );
}
